import type {
  LinkedAccount,
  Membership,
} from '@collinsonx/utils/generatedTypes/graphql';

import { AccountProvider, Client } from '@collinsonx/constants/enums';
import { getItem } from '@lib';
import { BookingError } from 'config/datadog';
import { IS_USER_VALIDATED } from 'config/storage';
import { JWTPayload } from 'jose';
import React, { createContext, useMemo, useState } from 'react';
import { RenderElement } from 'types/components';
import { Consumer } from 'types/consumer';

type Notification = {
  message: RenderElement;
};

type Error = {
  button?: string;
  custtomButtonClick?: () => void;
  key?: BookingError;
  message?: string;
  mobileMessage?: string;
  title?: string;
};

interface TokenPayload extends JWTPayload {
  accountProvider: AccountProvider;
  email?: string;
  externalId: string;
  firstName?: string;
  lastName?: string;
  membershipNumber: string;
  membershipType?: Client;
  sourceCode?: string;
}

export type Payload = {
  jwt: string;
  language: string;
  loungeCode: string;
  platform: string;
  referrer?: string;
  token: TokenPayload;
  version: string;
} & TokenPayload;

export type DomainContextProps = {
  consumerData: Consumer | null;
  error: Error | null;
  isUserValidated: boolean;
  matchedAccount: LinkedAccount | null;
  matchedMembership: Membership | null;
  notification: Notification | null;
  payload: null | Payload;

  setConsumerData: (val: Consumer) => void;
  setError: (val: Error | null) => void;
  setIsUserValidated: (val: boolean) => void;
  setMatchedAccount: (val: LinkedAccount) => void;
  setMatchedMembership: (val: Membership) => void;
  setNotification: (val: Notification | null) => void;
  setPayload: (val: Payload) => void;
};

const defaultContextValue = {
  consumerData: null,
  error: null,
  isUserValidated: false,
  matchedAccount: null,
  matchedMembership: null,
  notification: null,
  payload: null,
  setConsumerData: () => {},
  setError: () => {},
  setIsUserValidated: () => {},
  setMatchedAccount: () => {},
  setMatchedMembership: () => {},
  setNotification: () => {},
  setPayload: () => {},
};

export const DomainContext =
  createContext<DomainContextProps>(defaultContextValue);

const DomainProvider: React.FC<{
  children: RenderElement;
}> = ({ children }) => {
  const [error, setError] = useState<Error | null>(null);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [payload, setPayload] = useState<null | Payload>(null);
  const [matchedAccount, setMatchedAccount] = useState<LinkedAccount | null>(
    null
  );
  const [matchedMembership, setMatchedMembership] = useState<Membership | null>(
    null
  );
  const [consumerData, setConsumerData] = useState<Consumer | null>(null);
  const [isUserValidated, setIsUserValidated] = useState(
    () => getItem(IS_USER_VALIDATED) === 'true'
  );

  const domainContextValue = useMemo(
    () => ({
      consumerData,
      error,
      isUserValidated,
      matchedAccount,
      matchedMembership,
      notification,
      payload,
      setConsumerData,
      setError,
      setIsUserValidated,
      setMatchedAccount,
      setMatchedMembership,
      setNotification,
      setPayload,
    }),
    [
      consumerData,
      error,
      isUserValidated,
      matchedAccount,
      matchedMembership,
      notification,
      payload,
    ]
  );

  return (
    <DomainContext.Provider value={domainContextValue}>
      {children}
    </DomainContext.Provider>
  );
};

export default DomainProvider;
